import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Clock, DollarSign, Globe, Percent } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const teachingBenefits = [
  { icon: DollarSign, textKey: "home.becomeTutor.benefits.ownRates" },
  { icon: Clock, textKey: "home.becomeTutor.benefits.flexibleSchedule" },
  { icon: Globe, textKey: "home.becomeTutor.benefits.globalStudents" },
];

const weeklyEarnings = [
  { label: "Feb 2", value: 180 },
  { label: "Feb 9", value: 220 },
  { label: "Feb 16", value: 240 },
  { label: "Feb 23", value: 280 },
  { label: "Mar 2", value: 260 },
  { label: "Mar 9", value: 310 },
  { label: "Mar 16", value: 290 },
  { label: "Mar 23", value: 340 },
  { label: "Mar 30", value: 320 },
  { label: "Apr 6", value: 380 },
  { label: "Apr 13", value: 360 },
  { label: "Apr 20", value: 410 },
];

const BecomeTutor = () => {
  const { t } = useTranslation();
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-4">
              {t("home.becomeTutor.title")}{" "}
              <span className="text-primary">
                {t("home.becomeTutor.titleHighlight")}
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("home.becomeTutor.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Teach Live Classes */}
            <Card className="relative overflow-hidden border-border hover:border-foreground/20 transition-colors">
              <div className="aspect-2/1 bg-muted border-b border-border">
                <img src="/images/homepage/teach-live.webp" alt={t("home.becomeTutor.liveClasses.imageAlt")} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  {t("home.becomeTutor.liveClasses.heading")}
                </h3>

                <p className="text-lg text-foreground font-semibold mb-4">
                  {t("home.becomeTutor.liveClasses.tagline")}
                </p>

                <ul className="space-y-3 mb-8">
                  {teachingBenefits.map((benefit) => {
                    const Icon = benefit.icon;
                    return (
                      <li key={benefit.textKey} className="flex items-center gap-3 text-foreground text-sm">
                        <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                          <Icon className="w-4 h-4 text-foreground" />
                        </div>
                        {t(benefit.textKey)}
                      </li>
                    );
                  })}
                </ul>

                <Button variant="default" size="lg" asChild>
                  <Link to="/signup?role=tutor">
                    {t("home.becomeTutor.liveClasses.cta")}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Sell Courses */}
            <Card className="relative overflow-hidden border-border hover:border-foreground/20 transition-colors">
              <div className="aspect-2/1 bg-muted border-b border-border">
                <img src="/images/homepage/sell-courses.webp" alt={t("home.becomeTutor.sellCourses.imageAlt")} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-3">
                  {t("home.becomeTutor.sellCourses.heading")}
                </h3>

                <p className="text-lg text-foreground font-semibold mb-4">
                  {t("home.becomeTutor.sellCourses.tagline")}
                </p>

                <div className="bg-secondary/50 rounded-2xl p-5 mb-8">
                  <div className="flex items-center justify-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center">
                      <Percent className="w-7 h-7 text-foreground" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-foreground">90%</p>
                      <p className="text-sm text-muted-foreground">
                        {t("home.becomeTutor.sellCourses.revenueShare")}
                      </p>
                    </div>
                  </div>
                </div>

                <Button variant="outline" size="lg" asChild>
                  <Link to="/signup?role=tutor">
                    {t("home.becomeTutor.sellCourses.cta")}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>
  );
};

export default BecomeTutor;
